/**
 * Tâches & check-listes — constantes et helpers partagés (Batch #14.285).
 */
import { Bell, ClipboardCheck, ListChecks, PhoneCall } from "lucide-react";

export const TASK_STATUSES = [
  { id: "a_faire", label: "À faire", cls: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400", head: "bg-slate-50 border-slate-200 text-slate-700" },
  { id: "en_cours", label: "En cours", cls: "bg-sky-100 text-sky-800 border-sky-200", dot: "bg-sky-500", head: "bg-sky-50 border-sky-200 text-sky-800" },
  { id: "en_attente", label: "En attente", cls: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500", head: "bg-amber-50 border-amber-200 text-amber-800" },
  { id: "terminee", label: "Terminée", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500", head: "bg-emerald-50 border-emerald-200 text-emerald-800" },
];

export const TASK_TYPES = [
  { id: "tache", label: "Tâche", icon: ClipboardCheck },
  { id: "checklist", label: "Check-liste", icon: ListChecks },
  { id: "relance", label: "Relance", icon: PhoneCall },
  { id: "rappel", label: "Rappel", icon: Bell },
];

export const TASK_PRIORITIES = [
  { id: "urgente", label: "Urgente", cls: "bg-rose-100 text-rose-800 border-rose-200", dot: "bg-rose-500" },
  { id: "haute", label: "Haute", cls: "bg-orange-100 text-orange-800 border-orange-200", dot: "bg-orange-500" },
  { id: "normale", label: "Normale", cls: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400" },
  { id: "basse", label: "Basse", cls: "bg-slate-50 text-slate-500 border-slate-200", dot: "bg-slate-300" },
];

export const EXPERTISES = [
  { id: "cabinet", label: "Cabinet", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  { id: "fiscal", label: "Fiscal", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  { id: "immeuble", label: "Immeuble", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { id: "entrepreneurs", label: "Entrepreneurs", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  { id: "paie", label: "Paie", cls: "bg-pink-100 text-pink-800 border-pink-200" },
  { id: "tenue_livres", label: "Tenue de livres", cls: "bg-teal-100 text-teal-800 border-teal-200" },
];

/** Expertise Master (clé API) → expertise du module. */
export const MASTER_EXPERTISE = { fiscal: "fiscal", immeuble: "immeuble", autonome: "entrepreneurs", paie: "paie", tenue_livres: "tenue_livres" };

export const DUE_FILTERS = [
  ["", "Toutes les échéances"], ["overdue", "En retard"], ["today", "Aujourd'hui"], ["week", "7 prochains jours"], ["month", "30 prochains jours"], ["none", "Sans échéance"],
];

export const RECURRENCE_OPTIONS = [
  ["", "Aucune (ponctuelle)"], ["daily", "Quotidienne"], ["weekly", "Hebdomadaire"], ["biweekly", "Aux 2 semaines"], ["monthly", "Mensuelle"],
  ["quarterly", "Trimestrielle"], ["yearly", "Annuelle"], ["custom", "Personnalisée…"],
];
export const RECURRENCE_UNITS = [["days", "jour(s)"], ["weeks", "semaine(s)"], ["months", "mois"]];

/** Rappels d'échéance : minutes avant l'échéance. */
export const ALERT_OPTIONS = [
  { id: 10080, label: "1 semaine avant", short: "1 sem" }, { id: 4320, label: "3 jours avant", short: "3 j" }, { id: 1440, label: "1 jour avant", short: "1 j" },
  { id: 60, label: "1 heure avant", short: "1 h" }, { id: 0, label: "À l'échéance", short: "échéance" },
];
export const WEEKDAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("fr-CA", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export const meta = (list, id) => list.find((x) => x.id === id) || list[0];

export const fmtDue = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "short", year: "numeric" }) : "—");

export function dueInfo(t) {
  if (!t.due_date) return { text: "Sans échéance", cls: "text-slate-400" };
  const d = fmtDue(t.due_date) + (t.due_time ? ` ${t.due_time}` : "");
  if (t.status === "terminee") return { text: d, cls: "text-slate-500" };
  if (t.overdue) return { text: `${d} · ${-t.due_in} j de retard`, cls: "text-rose-700 font-semibold" };
  if (t.due_in === 0) return { text: `${d} · aujourd'hui`, cls: "text-amber-700 font-semibold" };
  if (t.due_in <= 7) return { text: `${d} · dans ${t.due_in} j`, cls: "text-amber-700" };
  return { text: d, cls: "text-slate-600" };
}

export const masterLabel = (m) => (m ? `${m.client_number ? `${m.client_number} · ` : ""}${m.couple_display_name || "(sans nom)"}` : "");

export function Progress({ done, total, className = "" }) {
  if (!total) return <span className="text-[11px] text-slate-300">—</span>;
  const pct = Math.round((100 * done) / total);
  return (
    <div className={`flex items-center gap-2 ${className}`} title={`${done}/${total} étapes`}>
      <div className="h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full transition-all ${pct === 100 ? "bg-emerald-500" : "bg-sky-500"}`} style={{ width: `${pct}%` }} /></div>
      <span className="text-[11px] tabular-nums text-slate-600">{done}/{total}</span>
    </div>
  );
}
